import { ChangeEvent, useContext, useEffect, useState } from 'react'
import { MeContext } from 'contexts/me'
import { useNavigate } from 'react-router-dom'
import { useNewTransferStore, useSelectedContactStore } from 'store'
import RoundedItem from 'components/RoundedItem'
import PrimaryButton from 'components/primaryButton'
import { trackEvent } from 'analytics/trackFunctions'
import SearchInput from 'components/SearchInput'
import ContactCard from 'components/ContactCard'
import { getRecentCashouts } from 'api/cashout'
import { accentFold, toTitleCase } from 'utils'
import { getExternalAccountContacts } from 'api/bank'
import Skeleton from 'react-loading-skeleton'
import EmptySection from 'components/EmptySection'

const TransferTab = () => {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [username, setUsername] = useState<string>(null)
  const [recentCashouts, setRecentCashouts] = useState([])
  const [contacts, setContacts] = useState([])
  const [filteredContacts, setFilteredContacts] = useState([])
  const { updateSelectedContact } = useSelectedContactStore()
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(true)

  const navigate = useNavigate()
  const { me } = useContext(MeContext)
  const { updateToDefault } = useNewTransferStore()

  useEffect(() => {
    if (me) {
      setUsername(`${me?.profile?.name?.split(' ')[0]} ${me?.profile?.surname}`)
    }
  }, [me])

  useEffect(() => {
    Promise.all([getRecentCashouts(), getExternalAccountContacts()])
      .then((res) => {
        const [recentsRes, contactsRes] = res
        if (recentsRes.success) {
          const { result } = recentsRes.data
          setRecentCashouts(result)
        }

        if (contactsRes.success) {
          const { result } = contactsRes.data

          const arr = Array.from(Object.entries(result), ([key, val]) => ({
            accountHolder: key,
            accounts: val,
            taxID: val[0].tax_identification_value,
            // case-folding and accent-folding for search
            normalizedAccountHolder: accentFold(key).toLowerCase()
          }))

          setContacts(arr)
          setFilteredContacts(arr)
        }
      })
      .finally(() => setIsLoadingContacts(false))
  }, [])

  const handleContactButton = ({ taxID, isOwn = false }) => {
    updateSelectedContact(taxID)

    isOwn
      ? navigate('/transferencias/contacto/mis-cuentas')
      : navigate('/transferencias/contacto')
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value
    setSearchInputValue(searchTerm)

    const normalizedInput = accentFold(searchTerm).toLowerCase()

    const filteredItems = contacts.filter((c) =>
      c.normalizedAccountHolder.includes(normalizedInput)
    )
    setFilteredContacts(filteredItems)
  }

  const handleCleanInput = () => {
    setSearchInputValue('')
    setFilteredContacts(contacts)
  }

  return (
    <>
      <h4 className='font-bold'>Recientes</h4>
      <div className='flex gap-2 items-center h-20 my-4'>
        {me && username ? (
          <UserButton
            name={username}
            title={
              <>
                Mis <br /> cuentas
              </>
            }
            onClick={() => {
              trackEvent({ eventName: 'bille_boton_mis_cuentas' })
              handleContactButton({
                taxID: me?.profile.tax_identification_value,
                isOwn: true
              })
            }}
          />
        ) : (
          <UserButtonSkeleton width={50} />
        )}
        <div className='w-[2px] h-[2.375rem] bg-gray-300 mx-2' />
        <div className='grid grid-cols-[repeat(3,_minmax(min-content,_4.5rem))] gap-x-2'>
          {isLoadingContacts ? (
            <>
              <UserButtonSkeleton width={50} />
              <UserButtonSkeleton width={50} />
              <UserButtonSkeleton width={50} />
            </>
          ) : (
            recentCashouts.slice(0, 4).map((item, idx) => (
              <UserButton
                areRecents
                name={item.account_holder}
                onClick={() =>
                  handleContactButton({
                    taxID: item.tax_identification_value,
                    isOwn:
                      item.tax_identification_value ===
                      me?.profile.tax_identification_value
                  })
                }
                key={item.tax_identification_value + idx || idx}
                title={<span>{toTitleCase(item.account_holder)}</span>}
              />
            ))
          )}
        </div>
      </div>
      <h4 className='font-bold'>Mis contactos</h4>
      {!isLoadingContacts && contacts.length > 0 && (
        <SearchInput
          value={searchInputValue}
          handler={(e) => handleSearch(e)}
          setValue={setSearchInputValue}
          handleCleanInput={handleCleanInput}
          onFocus={() => trackEvent({ eventName: 'bille_boton_buscar' })}
          className='my-4'
          placeholder='Buscar...'
        />
      )}
      <div className='md:max-h-[13.5rem] md:p-1 md:overflow-y-auto'>
        {isLoadingContacts && (
          <Skeleton count={3} className='w-full my-2' height={69} />
        )}
        {!isLoadingContacts && contacts.length > 0 ? (
          filteredContacts.map((c, idx) => (
            <ContactCard
              key={c.taxID + idx || idx}
              name={c.accountHolder}
              bankImages={c.accounts.map((acc) => acc.bank_image)}
              className='mb-3'
              onClick={() => handleContactButton({ taxID: c.taxID })}
            />
          ))
        ) : (
          <EmptySection section='CONTACTS' />
        )}
      </div>
      <PrimaryButton
        className='fixed shadow-navMob bottom-6 !w-[90vw] md:max-w-[32.725rem] md:static md:shadow-none md:mt-2'
        buttonText='Nueva transferencia'
        primaryButtonFunction={() => {
          trackEvent({
            eventName: 'bille_transferir_boton_nueva_transferencia'
          })
          updateToDefault()
          navigate('/transferencias/contactos')
        }}
      />
    </>
  )
}

export default TransferTab

const UserButton = ({
  name,
  onClick,
  title,
  areRecents = false
}: {
  name: string
  onClick: () => void
  title: JSX.Element | Element
  areRecents?: boolean
}) => (
  <button className='flex flex-col items-center' onClick={() => onClick()}>
    <RoundedItem
      roundedIconClassName={`!w-[2.5rem] !h-[2.5rem] ${
        !areRecents && '!bg-purple-900'
      }`}
      titleCase={false}
      hasTitle={false}
      name={name}
    />
    <p className='text-2xs leading-tight mt-2 text-center flex-grow flex items-center text-purple-900 font-semibold'>
      {title}
    </p>
  </button>
)

const UserButtonSkeleton = ({ width = 48 }: { width?: number }) => (
  <div className='flex flex-col w-full max-w-fit items-center justify-between'>
    <Skeleton circle height={40} className='mb-2' width={40} />
    <Skeleton width={width} height={25} />
  </div>
)
